import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { referralTemplatesLoad, referralTemplateUpdate, referralTemplateCreate } from '../../../redux/actions/referralActions';

function ReferralTemplatesModal({ isOpen, onClose, templates, fetchTemplates, staffId }) {
  const dispatch = useDispatch();
  const [view, setView] = useState('list'); // 'list' or 'edit'
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [optimisticStatus, setOptimisticStatus] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      dispatch(referralTemplatesLoad({rpp: 99, filter: { category: 'referrals', staff_id : staffId  } }));
    }
  }, [isOpen, dispatch]);
  if (!isOpen) return null;

  const handleEdit = (template) => {
    setErrors({});
    setEditingTemplate({ ...template });
    setView('edit');
  };

  const handleCreate = () => {
    setErrors({});
    setEditingTemplate({
      name: '',
      category: 'referrals',
      is_enabled: true,
      configuration: {
        template_category: 'Specialist Referrals',
        request_to_see: { value: '' },
        referral_reason: { value: '' },
        body_message: { value: '' }
      }
    });
    setView('edit');
  };

  const handleDuplicate = async (template) => {
    try {
      await dispatch(referralTemplateUpdate(template.id, { duplicate: true }));
      dispatch(referralTemplatesLoad({rpp: 99, filter: { category: 'referrals', staff_id : staffId  } }));
    } catch (error) {
      console.error("Error duplicating template", error);
    }
  };

  const handleToggleStatus = async (template) => {
    const isCurrentlyActive = optimisticStatus[template.id] !== undefined 
      ? optimisticStatus[template.id] 
      : template.active !== false && template.is_enabled !== false;
    
    const newStatus = !isCurrentlyActive;
    
    // Optimistic update for instant UI feedback
    setOptimisticStatus(prev => ({ ...prev, [template.id]: newStatus }));

    try {
      await dispatch(referralTemplateUpdate(template.id, { is_enabled: newStatus }));
      dispatch(referralTemplatesLoad({rpp: 99, filter: { category: 'referrals', staff_id : staffId  } }));
    } catch (error) {
      console.error("Error updating template status", error);
      // Revert optimistic update on error
      setOptimisticStatus(prev => ({ ...prev, [template.id]: isCurrentlyActive }));
    }
  };

  const handleSave = async () => {
    const newErrors = {};
    if (!editingTemplate.name?.trim()) newErrors.name = 'Required';
    if (!editingTemplate.configuration?.request_to_see?.value?.trim()) newErrors.request_to_see = 'Required';
    if (!editingTemplate.configuration?.referral_reason?.value?.trim()) newErrors.referral_reason = 'Required';
    if (!editingTemplate.configuration?.body_message?.value?.trim()) newErrors.body_message = 'Required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate.id) {
        await dispatch(referralTemplateUpdate(editingTemplate.id, editingTemplate));
      } else {
        await dispatch(
          referralTemplateCreate({
            name: editingTemplate.name,
            category: editingTemplate.category,
            is_enabled: editingTemplate.is_enabled,
            staff_id: staffId,
            configuration: editingTemplate.configuration
          })
        );
      }
      dispatch(referralTemplatesLoad({rpp: 99, filter: { category: 'referrals', staff_id : staffId } }));
      setView('list');
      setEditingTemplate(null);
    } catch (error) {
      console.error("Error saving template", error);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setErrors(prev => ({ ...prev, [field]: null }));
    if (['request_to_see', 'referral_reason', 'body_message'].includes(field)) {
      setEditingTemplate(prev => ({
        ...prev,
        configuration: {
          ...prev.configuration,
          [field]: { ...prev.configuration?.[field], value }
        }
      }));
    } else if (field === 'template_category') {
      setEditingTemplate(prev => ({
        ...prev,
        configuration: {
          ...prev.configuration,
          template_category: value
        }
      }));
    } else {
      setEditingTemplate((prev) => ({ ...prev, [field]: value }));
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show"></div>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ overflowY: 'auto' }}>
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header border-bottom-0 pb-0 mt-2 d-flex align-items-center">
              <h5 className="modal-title font-weight-bold">Manage Referral Templates</h5>
              {view === 'list' && (
                <button type="button" className="btn btn-sm btn-primary ml-auto mr-3" onClick={handleCreate}>Create</button>
              )}
              <button type="button" className={`close ${view !== 'list' ? 'ml-auto' : 'm-0'}`} onClick={onClose} aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            
            <div className="modal-body pt-0">
              {view === 'list' && (
                <div className="mt-4">
                  {Array.isArray(templates) && templates.map((template) => {
                    const isActive = optimisticStatus[template.id] !== undefined 
                      ? optimisticStatus[template.id] 
                      : template.active !== false && template.is_enabled !== false;
                    const isDisabled = !isActive;
                    
                    return (
                      <div key={template.id} className={`d-flex justify-content-between align-items-center border rounded p-3 mb-3 ${isDisabled ? 'bg-light opacity-50' : ''}`} style={{ opacity: isDisabled ? 0.6 : 1 }}>
                        <div>
                          <div className={`font-weight-bold ${isDisabled ? 'text-muted' : ''}`}>{template.name}</div>
                          <div className="text-muted small">
                            {template.configuration?.template_category || template.category} &middot; {isDisabled ? 'Disabled' : 'Active'}
                          </div>
                        </div>
                        <div>
                          <button type="button" className="badge badge-primary mr-3 pointer border-0" onClick={() => handleEdit(template)} disabled={isDisabled} title="Edit">
                            <i className="la la-pen font-size-18"></i>
                          </button>
                          <button type="button" className="badge badge-primary mr-3 pointer border-0" onClick={() => handleDuplicate(template)} title="Duplicate">
                            <i className="las la-copy font-size-18"></i>
                          </button>
                          <button
                            type="button"
                            className="badge badge-primary mr-3 pointer border-0"
                            onClick={() => handleToggleStatus(template)}
                            title={isDisabled ? 'Enable' : 'Disable'}
                          >
                            <i className={`la ${isDisabled ? 'la-eye-slash' : 'la-eye'} font-size-18`}></i>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {(!templates || templates.length === 0) && (
                    <div className="text-center text-muted py-5">No templates found.</div>
                  )}
                </div>
              )}

              {view === 'edit' && editingTemplate && (
                <div className="mt-4">
                  <h6 className="font-weight-bold mb-4 border-bottom pb-2">{editingTemplate.id ? 'Edit Template' : 'Create Template'}</h6>
                  <div className="row">
                    <div className="col-md-6 form-group">
                      <label className="text-muted small font-weight-bold">Template Name <span className="text-danger">*</span></label>
                      <input 
                        type="text" 
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`} 
                        value={editingTemplate.name || ''} 
                        onChange={(e) => handleChange('name', e.target.value)} 
                        required
                      />
                      {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                    </div>
                    <div className="col-md-6 form-group">
                      <label className="text-muted small font-weight-bold">Category <span className="text-danger">*</span></label>
                      <select 
                        className="custom-select" 
                        value={editingTemplate.configuration?.template_category || ''}
                        onChange={(e) => handleChange('template_category', e.target.value)}
                        required
                      >
                        <option value="Specialist Referrals">Specialist Referrals</option>
                        <option value="Urgent">Urgent</option>
                        <option value="Reports & Notes">Reports & Notes</option>
                      </select>
                    </div>
                    <div className="col-md-6 form-group">
                      <label className="text-muted small font-weight-bold">Request to See <span className="text-danger">*</span></label>
                      <input 
                        type="text" 
                        className={`form-control ${errors.request_to_see ? 'is-invalid' : ''}`} 
                        value={editingTemplate.configuration?.request_to_see?.value || ''} 
                        onChange={(e) => handleChange('request_to_see', e.target.value)} 
                        required
                      />
                      {errors.request_to_see && <div className="invalid-feedback">{errors.request_to_see}</div>}
                    </div>
                    <div className="col-md-6 form-group">
                      <label className="text-muted small font-weight-bold">Referral Reason <span className="text-danger">*</span></label>
                      <input 
                        type="text" 
                        className={`form-control ${errors.referral_reason ? 'is-invalid' : ''}`} 
                        value={editingTemplate.configuration?.referral_reason?.value || ''} 
                        onChange={(e) => handleChange('referral_reason', e.target.value)} 
                        required
                      />
                      {errors.referral_reason && <div className="invalid-feedback">{errors.referral_reason}</div>}
                    </div>
                    <div className="col-md-12 form-group">
                      <label className="text-muted small font-weight-bold">Body Message <span className="text-danger">*</span></label>
                      <textarea 
                        className={`form-control ${errors.body_message ? 'is-invalid' : ''}`} 
                        rows="5" 
                        value={editingTemplate.configuration?.body_message?.value || ''} 
                        onChange={(e) => handleChange('body_message', e.target.value)} 
                        required
                      ></textarea>
                      {errors.body_message && <div className="invalid-feedback">{errors.body_message}</div>}
                    </div>
                  </div>
                  <div className="d-flex justify-content-end mt-3 pb-3">
                    <button type="button" className="btn btn-outline-primary rounded-pill px-4 mr-2" onClick={() => setView('list')} disabled={saving}>Cancel</button>
                    <button type="button" className="btn btn-primary rounded-pill px-4" onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving...' : 'Save Template'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const mapStateToProps = state => {
  const { referral, exam } = state
  return {
    templates: referral.templates,
    staffId: exam.exam?.staff_id
  }
}

export default connect(mapStateToProps)(ReferralTemplatesModal);
